<template>
  <div
    class="d-flex flex-column align-stretch"
    style="gap: 24px; border-radius: 8px"
    :key="comparisonTableKey + 2"
  >
    <div class="container-plot">
      <div v-if="isCapturingPdf" class="capture-clone">
        Container para Retention Charts
        <div ref="retentionChartsCaptureContainer" class="retention-capture-mold">
          <v-row align="end">
            <v-col
              v-for="(item, index) in videosRetentionData"
              :key="`retention-${item.id || `placeholder-${index}`}`"
              cols="4"
              class="px-2"
              style="position: relative; overflow: hidden"
            >
              <RetentionChartVideoV3
                :retentionChartSeries="retentionChartSeries(index)"
                :m3u8Url="item.data.video.hls"
                :contentClass="'video-comparison-chart'"
              />
            </v-col>
          </v-row>
        </div>

        <div ref="pitchChartsCaptureContainer" class="retention-capture-mold">
          <v-row align="end">
            <v-col
              v-for="(item, index) in videosRetentionData"
              :key="`pitch-${item.id || `placeholder-${index}`}`"
              cols="4"
              class="px-2"
              style="position: relative; overflow: hidden"
            >
              <template v-if="item.pitch_data">
                <RetentionChartVideoV3
                  :key="pitchChartKey[index]"
                  :retentionChartSeries="retentionChartSeries(index)"
                  :m3u8Url="item.data.video.hls"
                  :contentClass="'video-comparison-chart'"
                  :annotationPoints="chartAnnotationPoints[index]"
                />
              </template>
              <template v-else>
                <RetentionChartVideoV3
                  :retentionChartSeries="retentionChartSeries(index)"
                  :m3u8Url="item.data.video.hls"
                  :contentClass="'video-comparison-chart'"
                />
              </template>
            </v-col>
          </v-row>
        </div>
      </div>

      <TableVideoComparer
        :items="comparisonMetricsByVideoId"
        :metrics="tableMetrics"
        :expandable="true"
        :loading="loading"
        :thumbnailRowHeight="86"
        :hasVideoFilterApplied="hasVideoFilterApplied"
        :key="comparisonTableKey"
      >
        <template #thumbnail="{ row }">
          <div class="video-thumbnail-custom">
            <template v-if="row.thumbnail_url">
              <div class="thumbnail-image-container">
                <img
                  :src="row.thumbnail_url"
                  :alt="`Thumbnail de ${row.title}`"
                  class="thumbnail-image"
                />
                <div class="play-overlay">
                  <div class="icon-overlay">
                    <v-icon
                      :color="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                      size="16"
                      @click="$root.$emit('openVideosFilterModal')"
                      >panda-edit</v-icon
                    >
                  </div>
                </div>
              </div>
            </template>
            <template>
              <div class="thumbnail-placeholder">
                <v-icon size="32" color="white">mdi-play-circle</v-icon>
              </div>
            </template>
          </div>
        </template>

        <template #playsCell="{ row }">
          <div
            class="custom-metric-value plays"
            :class="
              isBestPerformer(row, 'plays', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ Number(row.plays).toLocaleString('pt-BR') }}
          </div>
        </template>
        <template #unique_playsCell="{ row }">
          <div
            class="custom-metric-value unique-plays"
            :class="
              isBestPerformer(row, 'unique_plays', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ Number(row.unique_plays).toLocaleString('pt-BR') }}
          </div>
        </template>
        <template #impressionsCell="{ row }">
          <div
            class="custom-metric-value impressions"
            :class="
              isBestPerformer(row, 'impressions', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ Number(row.impressions).toLocaleString('pt-BR') }}
          </div>
        </template>
        <template #unique_impressionsCell="{ row }">
          <div
            class="custom-metric-value unique-impressions"
            :class="
              isBestPerformer(
                row,
                'unique_impressions',
                comparisonMetricsByVideoId
              )
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ Number(row.unique_impressions).toLocaleString('pt-BR') }}
          </div>
        </template>
        <template #play_rateCell="{ row }">
          <div
            class="custom-metric-value play-rate"
            :class="
              isBestPerformer(row, 'play_rate', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ row.play_rate }}%
          </div>
        </template>
        <template #retentionCell="{ row }">
          <div
            class="custom-metric-value retention"
            :class="
              isBestPerformer(row, 'retention_rate', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ row.retention_rate }}%
          </div>
        </template>
        <template #retention_pitchCell="{ row }">
          <div
            class="custom-metric-value retention-pitch"
            :key="retentionPitchRowKey"
            :class="
              isBestPerformer(
                row,
                'retention_pitch',
                comparisonMetricsByVideoId
              )
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ row.retention_pitch }}%
          </div>
        </template>
        <template #bandwidthCell="{ row }">
          <div
            class="custom-metric-value bandwidth"
            :class="
              isBestPerformer(row, 'bandwidth', comparisonMetricsByVideoId)
                ? 'highlight-best color-feedback-success-dark body-bold-smaller'
                : 'body-regular-smaller color-neutral-40-light'
            "
          >
            {{ formatBandwidth(row.bandwidth.sum_total) }}
          </div>
        </template>

        <template #expanded-content="{ metric }">
          <v-row align="center" no-gutters>
            <template v-if="barChartsConfig[metric.prop]">
              <v-col cols="10" style="margin-right: 5%; margin-left: 12%">
                <div class="expanded-chart-container">
                  <apexchart
                    :class="`${metric.prop}-apex-bar`"
                    class="apex-bar"
                    :type="barChartsConfig[metric.prop].options.chart.type"
                    height="244"
                    :options="barChartsConfig[metric.prop].options"
                    :series="barChartsConfig[metric.prop].series"
                  ></apexchart>
                </div>
              </v-col>
            </template>
            <template v-else-if="metric.prop === 'retention'">
              <v-col cols="2"></v-col>
              <v-col
                v-for="(item, index) in videosRetentionData"
                :key="item.id || `placeholder-${index}`"
                cols="3"
              >
                <RetentionChartVideoV3
                  :retentionChartSeries="retentionChartSeries(index)"
                  :m3u8Url="item.data.video.hls"
                  :contentClass="'video-comparison-chart'"
                />
              </v-col>
              <v-col cols="1"></v-col>
            </template>
            <template v-else-if="metric.prop === 'retention_pitch'">
              <v-col cols="2"></v-col>
              <v-col
                v-for="(item, index) in videosRetentionData"
                :key="item.id || `placeholder-${index}`"
                cols="3"
                class="pitch-retention"
                style="position: relative"
              >
                <template>
                  <template v-if="item.pitch_data">
                    <RetentionChartVideoV3
                      :key="pitchChartKey[index]"
                      :retentionChartSeries="retentionChartSeries(index)"
                      :m3u8Url="item.data.video.hls"
                      :contentClass="'video-comparison-chart'"
                      :annotationPoints="chartAnnotationPoints[index]"
                    />
                    <div class="pitch--edit">
                      <span class="body-regular-small color-neutral-40-light">{{
                        timeFormatter(item.pitch_data.time)
                      }}</span>
                      <v-btn
                        icon
                        small
                        class="edit-pitch-btn"
                        @click="openAddPitchModal(item)"
                      >
                        <v-icon
                          :color="$vuetify.theme.dark ? '#C2C5D6' : '#3F404C'"
                          size="16"
                          >panda-edit</v-icon
                        >
                      </v-btn>
                    </div>
                  </template>
                  <template v-else>
                    <RetentionChartVideoV3
                      :retentionChartSeries="retentionChartSeries(index)"
                      :m3u8Url="item.data.video.hls"
                      :contentClass="'video-comparison-chart'"
                    />
                    <div class="pitch--edit">
                      <v-btn
                        icon
                        small
                        class="add-pitch-btn"
                        @click="openAddPitchModal(item)"
                      >
                        <v-icon size="20"> panda-add </v-icon>
                        <span
                          class="body-regular-small color-neutral-40-light"
                          >{{ $t('message.add') }}</span
                        >
                      </v-btn>
                    </div>
                  </template>
                </template>
              </v-col>
              <v-col cols="1"></v-col>
            </template>
          </v-row>
        </template>
      </TableVideoComparer>
      <AddPitchModal :key="addPitchModalKey" ref="pitchSettingsModal" />
    </div>
  </div>
</template>

<script>
import TableVideoComparer from '@/views/AnalyticsV3/components/VideoComparer/TableVideoComparer.vue'
import VueApexCharts from 'vue-apexcharts'
import analyticsApi from '@/services/newAnalyticsApi'
import { mapState } from 'vuex'
import { format, addSeconds } from 'date-fns'
import RetentionChartVideoV3 from '@/views/AnalyticsV3/components/Charts/RetentionChartVideoV3.vue'
import AddPitchModal from '../Retention/modals/AddPitchModal.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { generatePdfComparerBlob } from '@/utils/pdfExporter.js'
import { toPng } from 'html-to-image'


export default {
  name: 'VideoComparer',
  components: {
    TableVideoComparer,
    apexchart: VueApexCharts,
    RetentionChartVideoV3,
    AddPitchModal,
  },
  data() {
    return {
      isCapturingPdf: false,
      loading: false,
      maxItems: 3,
      maxValue: 0,
      comparisonMetricsByVideoId: [],
      comparisonTableKey: 0,
      videosRetentionData: [],
      chartAnnotationPoints: [],
      videosTotalBandwidthData: [],
      retentionPitchData: [],
      pitchChartKey: {
        0: 0,
        1: 0,
        2: 0,
      },
      retentionPitchRowKey: 0,
      addPitchModalKey: Math.floor(Math.random() * 50000),
    }
  },
  computed: {
    ...mapState({
      selectedVideosToComparison: (state) =>
        state.analytics.filters.compareVideos.selectedVideos,
    }),
    dateRange() {
      return this.$store.state.analytics.filters.dateRange
    },
    hasVideoFilterApplied() {
      return !!this.selectedVideosToComparison?.length
    },
    checkSelectedVideosLength() {
      return (
        this.selectedVideosToComparison?.length === 2 ||
        this.selectedVideosToComparison?.length === 3
      )
    },
    getBandwidthLegendsConfig() {
      return {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '12px',
        labels: { colors: '#D1D1D1' },
        markers: {
          width: 12,
          height: 12,
          radius: 12,
        },
        itemMargin: {
          horizontal: 20,
          vertical: 8,
        },
        onItemClick: { toggleDataSeries: false },
        onItemHover: { highlightDataSeries: false },
      }
    },
    chartDataItems() {
      const items = [...this.comparisonMetricsByVideoId]
      const placeholdersNeeded = this.maxItems - items.length

      for (let i = 0; i < placeholdersNeeded; i++) {
        items.push({
          id: `placeholder-${i}`,
          title: 'Empty Slot',
          isPlaceholder: true,
          plays: 0,
          unique_plays: 0,
          impressions: 0,
          unique_impressions: 0,
          play_rate: 0,
          retention_rate: 0,
          retention_pitch: 0,
          bandwidth: { sum_total: 0, sum_b: 0, sum_download: 0, sum_drm: 0 },
        })
      }

      return items
    },
    barChartsConfig() {
      const config = {}
      this.tableMetrics.forEach((metric, index) => {
        let chartOptions = null
        const barColor = this.$vuetify.theme.dark ? '#697AF2' : '#3F55EE'
        switch (metric.prop) {
          case 'plays':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: [barColor],
              columnWidth: '35%',
              index: index,
            })
            break

          case 'unique_plays':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: [barColor],
              columnWidth: '35%',
              index: index,
            })
            break

          case 'impressions':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: [barColor],
              columnWidth: '35%',
              index: index,
            })
            break

          case 'unique_impressions':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: [barColor],
              columnWidth: '35%',
              index: index,
            })
            break

          case 'play_rate':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: [barColor],
              columnWidth: '35%',
              index: index,
            })
            break

          case 'bandwidth':
            chartOptions = this.getBarChartOptions({
              metric,
              colors: ['#5974FF', '#1ECCBE', '#FF9341', '#9C27B0'],
              columnWidth: '35%',
              index: index,
              stacked: true,
            })
            break
        }

        config[metric.prop] = chartOptions
      })

      return config
    },
    tableMetrics() {
      return [
        {
          prop: 'plays',
          label: this.$t('message.pages.analyticsV3.videoComparer.plays.label'),
          type: 'number',
          info: this.$t('message.pages.analyticsV3.videoComparer.plays.info'),
        },
        {
          prop: 'unique_plays',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.uniquePlays.label'
          ),
          type: 'number',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.uniquePlays.info'
          ),
        },
        {
          prop: 'impressions',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.impressions.label'
          ),
          type: 'number',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.impressions.info'
          ),
        },
        {
          prop: 'unique_impressions',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.uniqueImpressions.label'
          ),
          type: 'number',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.uniqueImpressions.info'
          ),
        },
        {
          prop: 'play_rate',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.playRate.label'
          ),
          type: 'percentage',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.playRate.info'
          ),
        },
        {
          prop: 'retention',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.retention.label'
          ),
          type: 'percentage',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.retention.info'
          ),
        },
        {
          prop: 'retention_pitch',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.retentionPitch.label'
          ),
          type: 'percentage',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.retentionPitch.info'
          ),
        },
        {
          prop: 'bandwidth',
          label: this.$t(
            'message.pages.analyticsV3.videoComparer.bandwith.label'
          ),
          type: 'bytes',
          info: this.$t(
            'message.pages.analyticsV3.videoComparer.bandwith.info'
          ),
        },
      ]
    },
  },
  created() {
    this.$root.$on('handleVideosNewComparison', () => {
      this.comparisonTableKey++
      this.comparisonMetricsByVideoId = []
      this.videosRetentionData = []
      this.chartAnnotationPoints = []
      this.retentionPitchData = []
      this.videosTotalBandwidthData = []
      this.loadMetrics()
    })
  },
  beforeDestroy() {
    this.removeComparisonPageViewClass()
    this.$root.$emit('updateHeaderTitle', null)
    this.$root.$off('closeAddPitchModalData')
    this.$root.$off('handleVideosNewComparison')
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-pdf-export', this.generateAndDownloadPdf)
  },
  watch: {
    '$i18next.resolvedLanguage': {
      handler() {
        this.updateHeaderTitle()
      },
    },
    dateRange: {
      deep: true,
      handler() {
        this.$root.$emit('handleVideosNewComparison')
      },
    },
  },
  mounted() {
    this.addComparisonPageViewClass()
    this.$root.$on('analyticsHeaderMounted', () => {
      this.updateHeaderTitle()
    })
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-pdf-export', this.generateAndDownloadPdf)
    this.updateHeaderTitle()
    this.loadMetrics()
    if (this.$route.params.openVideosFilterModal) {
      setTimeout(() => {
        this.$root.$emit('openVideosFilterModal')
      }, 1000)
    }
  },
  methods: {
    updateAnnotationPoint(index, updatedMarkers) {
      const xPitchMarker = Math.round(updatedMarkers.time / 5) * 5
      const yPitchMarker =
        this.videosRetentionData[index].data.retention[xPitchMarker]
      const pitchMarkers = {
        x: xPitchMarker,
        y: yPitchMarker,
        marker: {
          size: 6,
          strokeColor: '#fff',
          fillColor: '#3F55EE',
          strokeWidth: 1,
        },
      }
      this.$set(this.chartAnnotationPoints[index], 0, pitchMarkers)
    },
    openAddPitchModal(item) {
      this.$root.$emit('openAddPitchModal', item.data)
      this.$refs.pitchSettingsModal.$on('updatedMarkers', (updatedMarkers) => {
        const newPitchMarker = updatedMarkers.find((o) => o.type === 'pitch')
        const findId = (obj) => obj.id === item.id
        const index = this.videosRetentionData.findIndex(findId)
        this.videosRetentionData[index].pitch_data = newPitchMarker
        const otherMarkers = this.videosRetentionData[
          index
        ].data.video.retention_markers.filter((o) => o.type !== 'pitch')
        this.videosRetentionData[index].data.video.retention_markers = [
          ...otherMarkers,
          newPitchMarker,
        ]
        const retentionPitch = this.updateCalcRetentionPitch(
          newPitchMarker,
          item
        )
        this.comparisonMetricsByVideoId[index].retention_pitch = retentionPitch
        this.updateAnnotationPoint(index, newPitchMarker)
        this.$refs.pitchSettingsModal.$off('updatedMarkers')
        this.retentionPitchRowKey++
        this.pitchChartKey[index]++
        this.addPitchModalKey++
      })
    },
    timeFormatter: (val) => {
      if (val) {
        if (val > 3600)
          return format(
            addSeconds(new Date('2022/01/01 00:00'), val),
            'HH:mm:ss'
          )
        return format(addSeconds(new Date('2022/01/01 00:00'), val), 'H:mm:ss')
      } else {
        return format(new Date('2022/01/01 00:00'), 'H:mm:ss')
      }
    },
    updateHeaderTitle() {
      this.$root.$emit(
        'updateHeaderTitle',
        this.$i18next.t('message.pages.analyticsV3.videoComparer.headerTitle')
      )
    },
    handleBarChartsConfig(metric, data) {
      if (metric.prop === 'bandwidth') {
        let playData = []
        let downloadData = []
        let drmData = []
        this.videosTotalBandwidthData.forEach((item) => {
          playData.push(item.data.sum_b)
          downloadData.push(item.data.sum_download)
          drmData.push(item.data.sum_drm)
        })
        const placeholdersNeeded =
          this.maxItems - this.videosTotalBandwidthData.length

        for (let i = 0; i < placeholdersNeeded; i++) {
          playData.push(null)
          downloadData.push(null)
          drmData.push(null)
        }
        const series = [
          { name: 'Play', data: playData },
          { name: 'Download', data: downloadData },
          { name: 'DRM', data: drmData },
        ]
        return series
      }
      const series = [
        {
          name: metric.label,
          data: data,
        },
      ]
      return series
    },
    retentionChartSeries(index) {
      return [
        {
          name: this.$i18next.t(
            'message.pages.video.metricsPage.retentionRate'
          ),
          data: this.aggregateRetention(
            this.videosRetentionData[index].data.retention
          ),
        },
        {
          name: this.$i18next.t('message.pages.video.metricsPage.viewsPerTime'),
          data: this.aggregateRetention(
            this.videosRetentionData[index].data.views_data
          ),
        },
      ]
    },
    aggregateRetention(retention, duration) {
      if (this.maxValue === 0) {
        if (Math.max(...Object.values(retention)) === 0) {
          this.maxValue = 10
        } else {
          this.maxValue = Math.max(...Object.values(retention))
        }
      }

      if (Object.keys(retention).length <= 1) {
        const videoSize = duration
        for (let i = 0; i <= videoSize + 5; i += 5) {
          const missingToVideoSize = videoSize - i
          retention[i] = 0
          if (missingToVideoSize < 5) {
            retention[missingToVideoSize] = 0
            break
          }
        }
      }

      return Object.keys(retention)
        .map((item) => ({
          x: Number(item),
          y: retention[item],
        }))
        .sort(function (a, b) {
          return a.x - b.x
        })
    },
    normalizeRetentionData(retention) {
      const {
        video: { duration },
        retention: retentionObj,
        views_data,
      } = retention
      const normRetentionObj = this.normalizeData(duration, retentionObj)
      const normViewsDataObj = this.normalizeData(duration, views_data)
      return {
        retention: normRetentionObj,
        video: retention.video,
        views_data: normViewsDataObj,
      }
    },
    normalizeData(duration, retentionObj, step = 5) {
      const filled = {}
      const maxTimestamp = Math.floor(duration / step) * step
      for (let t = 0; t <= maxTimestamp; t += step) {
        filled[t] = Object.prototype.hasOwnProperty.call(retentionObj, t)
          ? retentionObj[t]
          : 0
      }

      return filled
    },
    setBackgroundChartColor(index) {
      if (index % 2 != 0) return 'transparent'
      return this.$vuetify.theme.dark ? '#2C2E3A' : '#F9F9FB'
    },
    getBarChartOptions({
      metric,
      colors,
      columnWidth,
      index,
      stacked = false,
    }) {
      const data = this.chartDataItems.map((item) => item[metric.prop] || 0)

      const series = this.handleBarChartsConfig(metric, data)
      const options = this.handleBarChartOptions(
        metric,
        colors,
        columnWidth,
        index,
        stacked
      )

      return { series, options }
    },
    handleBarChartOptions(metric, colors, columnWidth, index, stacked) {
      let backgroundChartColor = this.setBackgroundChartColor(index)
      const _this = this
      let _legends = { show: false }
      if (metric.prop === 'bandwidth') {
        _legends = this.getBandwidthLegendsConfig
      }
      const _formatter = function (val) {
        if (metric.prop === 'bandwidth') return _this.formatBandwidth(val)
        return val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val
      }
      const categories = this.chartDataItems.map((item) =>
        item.isPlaceholder ? '' : item.title
      )
      const options = {
        chart: {
          type: 'bar',
          height: 244,
          width: '100%',
          toolbar: { show: false },
          offsetX: 0,
          background: backgroundChartColor,
          stacked: stacked,
        },
        states: {
          hover: {
            filter: { type: 'none', value: 0 },
          },
          active: {
            filter: { type: 'none', value: 0 },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: columnWidth,
            distributed: false,
            borderRadius: 0,
          },
        },
        colors: colors,
        dataLabels: {
          enabled: false,
        },
        legend: _legends,
        xaxis: {
          categories: categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          crosshairs: {
            show: false,
          },
        },
        yaxis: {
          show: true,
          labels: {
            style: { colors: this.$vuetify.theme.dark ? '#B9BBC6' : '#636578' },
            formatter: (val) => _formatter(val),
          },
        },
        grid: {
          show: true,
          borderColor: this.$vuetify.theme.dark ? '#363949' : '#EDEEF1',
          strokeDashArray: 0,
        },
        tooltip: {
          enabled: true,
          theme: this.$vuetify.theme.dark ? 'dark' : 'light',
          onDatasetHover: {
            highlightDataSeries: false,
          },
          shared: true,
          intersect: false,
          hideEmptySeries: true,
        },
        responsive: [
          {
            breakpoint: 1400,
            options: {
              chart: {
                width: '100%',
                offsetX: -20,
              },
              plotOptions: {
                bar: {
                  columnWidth: '35%',
                },
              },
            },
          },
          {
            breakpoint: 768,
            options: {
              chart: {
                width: '100%',
                offsetX: 10,
              },
              plotOptions: {
                bar: {
                  columnWidth: '30%',
                },
              },
              yaxis: {
                labels: {
                  style: {
                    colors: {
                      colors: this.$vuetify.theme.dark ? '#B9BBC6' : '#636578',
                    },
                    fontSize: '10px',
                  },
                  formatter: (val) =>
                    val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val,
                },
              },
            },
          },
          {
            breakpoint: 480,
            options: {
              chart: {
                width: '100%',
                offsetX: 0,
              },
              plotOptions: {
                bar: {
                  columnWidth: '40%',
                },
              },
              yaxis: {
                labels: {
                  style: {
                    colors: {
                      colors: this.$vuetify.theme.dark ? '#B9BBC6' : '#636578',
                    },
                    fontSize: '9px',
                  },
                  formatter: (val) =>
                    val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val,
                },
              },
            },
          },
        ],
      }
      return options
    },
    formatBandwidth(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
    },

    getValues(items, prop) {
      return items.map((item) => item[prop]).filter((val) => val != null)
    },

    isBestPerformer(item, prop, allItems) {
      const values = this.getValues(allItems, prop)
      let bandwidthValues

      if (prop === 'bandwidth') {
        bandwidthValues = values.flatMap((item) => item.sum_b)
      }

      if (values.length === 0) {
        return false
      }

      const bestValue =
        prop === 'bandwidth'
          ? Math.min(...bandwidthValues)
          : Math.max(...values)
      if (prop === 'bandwidth') return item[prop].sum_total === bestValue
      return item[prop] === bestValue
    },

    async loadMetrics() {
      if (!this.checkSelectedVideosLength) return
      this.loading = true
      const { start: startDate, end: endDate } = this.dateRange

      try {
        const perVideoPromises = this.selectedVideosToComparison.map(
          ({ videoId }) =>
            this.fetchAllMetricsForVideo(videoId, startDate, endDate)
        )
        const settled = await Promise.allSettled(perVideoPromises)

        settled.forEach((res) => {
          if (res.status !== 'fulfilled') {
            console.error('Erro ao buscar métricas:', res.reason)

            return
          }

          const {
            videoId,
            metrics,
            retention,
            bandwidth: { data: bandwidthByDay },
          } = res.value

          const totalBandwith = this.sumBandwidth(bandwidthByDay)
          const normalizedRentention = this.normalizeRetentionData(retention)

          const pitchData = this.getRetentionPitchData(normalizedRentention)

          this.$set(this.videosRetentionData, this.videosRetentionData.length, {
            id: videoId,
            data: normalizedRentention,
            pitch_data: pitchData,
          })
          this.$set(
            this.videosTotalBandwidthData,
            this.videosTotalBandwidthData.length,
            {
              id: videoId,
              data: totalBandwith,
            }
          )

          const { pullzone_name, video_external_id, title } =
            this.selectedVideosToComparison.find((v) => v.videoId === videoId)

          const playRate = this.calcPlayRate(
            metrics.total.unique_play,
            metrics.total.unique_view
          )
          const retentionRate = this.calcRetentionRate(normalizedRentention)
          const retentionPitch = this.calcRetentionPitch(normalizedRentention)

          this.$set(
            this.comparisonMetricsByVideoId,
            this.comparisonMetricsByVideoId.length,
            {
              id: videoId,
              title,
              thumbnail_url: `https://cdn.pandavideo.com/${pullzone_name}/${video_external_id}/thumbnail.jpg`,
              plays: metrics.total.play,
              unique_plays: metrics.total.unique_play,
              impressions: metrics.total.view,
              unique_impressions: metrics.total.unique_view,
              play_rate: playRate,
              retention_rate: retentionRate,
              retention_pitch: retentionPitch,
              bandwidth: totalBandwith,
            }
          )
        })
      } finally {
        this.handlePitchAnnotationPoints()
        this.loading = false
      }
    },
    async fetchAllMetricsForVideo(videoId, startDate, endDate) {
      const trafficEndDate = format(endDate, 'yyyy-MM-dd')
      const trafficStartDate = format(startDate, 'yyyy-MM-dd')

      const [resMetrics, resRetention, resBandwidth] = await Promise.all([
        analyticsApi.getSingleVideoAnalytics(videoId, startDate, endDate),
        analyticsApi.getVideoMetricsV2(videoId, startDate, endDate),
        analyticsApi.getVideoTrafficBandwidthTypeAll(
          trafficStartDate,
          trafficEndDate,
          videoId
        ),
      ])

      return {
        videoId,
        metrics: resMetrics.data,
        retention: resRetention.data,
        bandwidth: resBandwidth.data,
      }
    },
    calcPlayRate(unique_play, unique_view) {
      const playRate =
        unique_view === 0 ? 0 : ((unique_play * 100) / unique_view).toFixed(1)
      return Number(playRate)
    },
    addComparisonPageViewClass() {
      const pageViewElement = document.querySelector('.page-view__container')
      pageViewElement.classList.add('page-view__comparison')
    },
    removeComparisonPageViewClass() {
      const pageViewElement = document.querySelector('.page-view__comparison')
      pageViewElement.classList.remove('page-view__comparison')
    },
    calcRetentionRate(data) {
      let engagementCalc
      let maxPlays = Object.keys(data.retention).length
        ? Object.values(data.retention).reduce((a, b) => {
            return a > b ? a : b
          })
        : 0
      if (Object.values(data.retention).length) {
        const totalRetention =
          Object.values(data.retention).reduce((a, b) => a + b, 0) * 5
        const videoDuration = parseInt(Object.keys(data.retention).pop()) + 5
        engagementCalc = (totalRetention / maxPlays / videoDuration) * 100
        if (!engagementCalc) engagementCalc = 0
      } else {
        return 0
      }
      return Number(engagementCalc.toFixed(1))
    },
    getRetentionPitchData(data) {
      const { retention_markers } = data.video
      if (!retention_markers.length) return null
      const pitchData = retention_markers.filter((obj) => obj.type === 'pitch')
      return pitchData.length ? pitchData[0] : null
    },
    calcRetentionPitch(data) {
      const pitchData = this.getRetentionPitchData(data)
      if (!pitchData) return 0
      const pitchTime = Math.round(pitchData.time / 5) * 5
      const retentionData = data.retention[pitchTime]
      let maxPlays = Object.values(data.retention).sort((a, b) => b - a)[0]
      if (maxPlays === 0) maxPlays = 1
      const percent = (retentionData * 100) / maxPlays
      return Math.round(percent)
    },
    updateCalcRetentionPitch(data, item) {
      const pitchTime = Math.round(data.time / 5) * 5
      const retentionData = item.data.retention[pitchTime]
      let maxPlays = Object.values(item.data.retention).sort((a, b) => b - a)[0]
      if (maxPlays === 0) maxPlays = 1
      const percent = (retentionData * 100) / maxPlays
      return Math.round(percent)
    },
    handlePitchAnnotationPoints() {
      for (let i = 0; i < this.videosRetentionData.length; i++) {
        const annotationPoints = this.buildAnnotationPointsObj(
          this.videosRetentionData[i]
        )
        this.$set(this.chartAnnotationPoints, i, annotationPoints)
      }
    },
    buildAnnotationPointsObj(retentionData) {
      const ret = []
      const pitchMarker = retentionData.pitch_data
      if (pitchMarker) {
        const xPitchMarker = Math.round(pitchMarker.time / 5) * 5
        const yPitchMarker = retentionData.data.retention[xPitchMarker]
        ret.push({
          x: xPitchMarker,
          y: yPitchMarker,
          marker: {
            size: 6,
            strokeColor: '#fff',
            fillColor: '#3F55EE',
            strokeWidth: 1,
          },
        })
      }
      return ret
    },
    sumBandwidth(arr) {
      const sum_b = arr.reduce((total, item) => total + (item?.b ?? 0), 0)
      const sum_download = arr.reduce(
        (total, item) => total + (item?.b_download ?? 0),
        0
      )
      const sum_drm = arr.reduce((total, item) => total + (item?.b_drm ?? 0), 0)
      const sum_total = sum_b + sum_download + sum_drm
      return { sum_b, sum_download, sum_drm, sum_total }
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const filename = `comparativo-videos-${startDateFormatted}--${endDateFormatted}.csv`

      const columns = [
        { text: 'Métrica', value: 'metric' },
        ...this.comparisonMetricsByVideoId.map(video => ({
          text: video.title,
          value: `video_${video.id}` 
        }))
      ]

      const items = this.tableMetrics.map(metric => {
        const rowObject = {
          metric: metric.label
        }

        this.comparisonMetricsByVideoId.forEach(video => {
          const prop = metric.prop
          const value = video[prop]
          let formattedValue = '-'

          if (value !== undefined) {
            switch (prop) {
              case 'bandwidth':
                formattedValue = this.formatBandwidth(value.sum_total)
                break
              case 'play_rate':
              case 'retention_rate':
              case 'retention_pitch':
                formattedValue = `${value}%`
                break
              default:
                formattedValue = Number(value).toLocaleString('pt-BR')
                break
            }
          }
          rowObject[`video_${video.id}`] = formattedValue
        })

        return rowObject
      })

      exportToCsv(filename, columns, items)
    },

    getBestPerformerValues() {
      const bestPerformers = {}
      if (this.comparisonMetricsByVideoId.length === 0) {
        return bestPerformers
      }
      this.tableMetrics.forEach(metric => {
        const prop = metric.prop
        if (prop === 'bandwidth') {
          const allValues = this.comparisonMetricsByVideoId.map(v => v.bandwidth.sum_total)
          bestPerformers[prop] = Math.min(...allValues)
        } else {
          const allValues = this.comparisonMetricsByVideoId.map(v => v[prop])
          bestPerformers[prop] = Math.max(...allValues)
        }
      })
      return bestPerformers
    },

    async generateAndDownloadPdf() {
      this.isCapturingPdf = true
      const capturedCharts = []

      try {
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 3000)) 

        const captureChart = async (containerRef, title) => {
          const container = this.$refs[containerRef]
          if (container) {
            try {
              const imgUri = await toPng(container, {
                backgroundColor: '#FFFFFF',
                pixelRatio: 4,
                quality: 1,
                skipFonts: true,
                cacheBust: true,
              })
              capturedCharts.push({ title, imageData: imgUri })
            } catch (err) {
              console.error(`Erro ao capturar ${containerRef}:`, err)
            }
          } else {
            console.warn(`Container ${containerRef} não encontrado.`)
          }
        }

        await captureChart(
          'retentionChartsCaptureContainer',
          this.$t('message.pages.analyticsV3.videoComparer.retention.label')
        )
        await captureChart(
          'pitchChartsCaptureContainer',
          this.$t('message.pages.analyticsV3.videoComparer.retentionPitch.label')
        )

        this.isCapturingPdf = false

        const bestPerformers = this.getBestPerformerValues()
        const highlightTextColor = '#006400'

        const customCellStyler = (data) => {
          if (data.section !== 'body' || data.column.index === 0) return
          const metric = this.tableMetrics[data.row.index]
          const video = this.comparisonMetricsByVideoId[data.column.index - 1]
          if (!metric || !video) return
          const prop = metric.prop
          const bestValue = bestPerformers[prop]
          const currentValue = prop === 'bandwidth' ? video.bandwidth.sum_total : video[prop]
          if (currentValue === bestValue) {
            data.cell.styles.textColor = highlightTextColor
            data.cell.styles.fontStyle = 'bold'
          }
        }

        const headerData = {
          title: this.$t('message.pages.analyticsV3.videoComparer.headerTitle'),
          description: 'Comparativo de métricas por vídeo.',
          metrics: [],
        }
        const tableHeaders = ['Métrica', ...this.comparisonMetricsByVideoId.map(v => v.title)]
        const tableBody = this.tableMetrics.map(metric => {
          const metricLabel = metric.label
          const metricValues = this.comparisonMetricsByVideoId.map(video => {
            const prop = metric.prop
            const value = video[prop]
            if (value === undefined) return '-'
            switch (prop) {
              case 'bandwidth':
                return this.formatBandwidth(value.sum_total)
              case 'play_rate':
              case 'retention_rate':
              case 'retention_pitch':
                return `${value}%`
              default:
                return Number(value).toLocaleString('pt-BR')
            }
          })
          return [metricLabel, ...metricValues]
        })

        const columnStyles = {
          0: {
            cellWidth: 'auto',
          },
          1: { cellWidth: 55, overflow: 'ellipsize' },
          2: { cellWidth: 55, overflow: 'ellipsize' },
          3: { cellWidth: 55, overflow: 'ellipsize' },
        }

        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const filename = `comparativo-videos-${startDateFormatted}--${endDateFormatted}.pdf`

        const hasTwoVideos = this.comparisonMetricsByVideoId.length === 2
        const pdfBlob = await generatePdfComparerBlob({
          chartHeaderData: headerData,
          tableHeaders,
          tableBody,
          columnStyles,
          customCellStyler,
          capturedChartImages: capturedCharts,
          hasTwoVideos: hasTwoVideos 
        })

        this.$root.$emit('pdf-ready', { blob: pdfBlob, filename })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
      } finally {
        this.isCapturingPdf = false 
      }
    },
  },
}
</script>

<style lang="scss">
.page-view__container.page-view__comparison {
  .page-view__header {
    z-index: 18;
    border-bottom: 1px solid !important;
    border-color: varFigma(neutral-95-lightest) !important;
  }
  .page-view__content {
    background-color: varFigma(neutral-100-white) !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    .container-plot {
      align-items: center;
      .base-table-wrapper {
        max-width: 1540px;
      }
    }
    .tabs-container {
      display: none !important;
    }
  }
}

.apexcharts-tooltip {
  background-color: varFigma(neutral-100-white) !important;
  color: varFigma(neutral-20-dark) !important;
  border: 1px solid varFigma(neutral-95-lightest) !important;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 217px !important;
  max-width: 217px !important;
}
.apexcharts-tooltip-title {
  background-color: varFigma(neutral-95-lightest) !important;
  color: varFigma(neutral-20-dark) !important;
  font-weight: 500;
  padding: 8px 16px !important;
  border-bottom: 1px solid varFigma(neutral-95-lightest) !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
.apex-bar.bandwidth-apex-bar {
  .apexcharts-legend {
    display: flex !important;
    .apexcharts-legend-text {
      color: varFigma(neutral-20-dark) !important;
      font-family: Rubik !important;
      font-size: 12px !important;
      font-style: normal !important;
      font-weight: 400 !important;
      line-height: 150% !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.container-plot {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  gap: 24px;
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);
}
.apex-bar::v-deep {
  width: 100%;
  svg {
    transform: translate(0, 0) !important;
  }
}
.highlight-best {
  display: flex;
  padding: 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: varFigma(feedback-success-light);
}
.expanded-chart-container {
  background-color: tranparent;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: start;
  padding: 0;
  margin: 0;
}
.video-thumbnail-custom {
  height: 100%;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.video-thumbnail-custom .thumbnail-image-container {
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
}
.thumbnail-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: $spacing-02;
}
.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.51) 0%,
    rgba(0, 0, 0, 0.51) 100%
  );
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  .icon-overlay {
    display: flex;
    width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;
    border-radius: $spacing-02;
    background: varFigma(neutral-95-lightest);
  }
}
.thumbnail-image-container:hover .play-overlay {
  opacity: 1;
  pointer-events: auto;
  cursor: pointer;
}
.color-neutral-40-light {
  color: varFigma(neutral-40-light);
}
.color-feedback-success-dark {
  color: varFigma(feedback-success-dark);
}
.pitch--edit {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  bottom: 0;
  .edit-pitch-btn {
    display: flex;
    width: 32px;
    height: 32px;
    justify-content: center;
    align-items: center;
    border-radius: $spacing-03;
    background: varFigma(neutral-95-lightest);
    margin-left: $spacing-03;
  }
  .add-pitch-btn {
    display: flex;
    height: 32px;
    width: fit-content;
    min-width: 80px;
    padding: 0px $spacing-04;
    justify-content: center;
    align-items: center;
    gap: $spacing-05;
    background: varFigma(neutral-95-lightest);
    border-radius: $spacing-03;
    ::v-deep.v-btn__content {
      text-transform: none;
      i {
        margin-right: 6px;
      }
    }
  }
}
.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  z-index: 99;
  pointer-events: none;
}

.retention-capture-mold {
  width: 100%;
  height: 300px; 
  box-sizing: border-box;
}

</style>
